/** Service Desk console: Settings -> Connectors (SD-SC-17), part C: secrets, authentication and OAuth connection. */
export const serviceConnectorsC = {
  secrets: {
    title: "Secrets",
    intro: "Sealed in the workspace vault and write-only: once saved, only the last four characters are shown. Rotating keeps the old value working for a while so running calls are not cut off.",
    empty: "No secrets yet. Authentication that needs a key, token or password will ask for it here.",
    add: "Add secret",
    set: "Set value",
    setAria: "Set a value for {{name}}",
    setTitle: "Add a secret",
    rotate: "Rotate",
    rotateAria: "Rotate {{name}}",
    rotateTitle: "Rotate {{name}}",
    revoke: "End overlap now",
    revokeAria: "End the overlap of {{name}} now",
    revoked: "The previous value of {{name}} no longer works",
    writeOnly: "Write-only: the value is sealed when you save and is never shown again.",
    slot: "Slot name",
    slotHint: "The name the authentication or a template reads, like api_key.",
    nameInvalid: "Use lowercase letters, numbers and underscores, starting with a letter.",
    value: "Secret value",
    valueRequired: "Enter the secret value.",
    expires: "Expires on (optional)",
    expiresHint: "You are reminded before the credential stops working.",
    expiresOn: "expires {{date}}",
    overlap: "Keep the old value working for (hours)",
    overlapHint: "0 ends it at once. Up to 168 hours.",
    overlapInvalid: "Use a whole number from 0 to 168.",
    saved: "{{name}} saved",
    rotated: "{{name}} rotated",
    setAt: "Set {{date}}",
    rotatedAt: "Rotated {{date}}",
    lastFour: "Last four characters",
    previousActive: "Old value still valid",
    neededNotSet: "Needed by the authentication, not set yet",
    notSet: "Not set",
    missingCount_one: "{{count}} secret missing",
    missingCount_other: "{{count}} secrets missing",
    deleteAria: "Delete {{name}}",
    deleteTitle: "Delete the secret {{name}}?",
    deleteBody: "Calls that read this slot fail until a new value is set.",
    deleteConfirm: "Delete secret",
    deleted: "{{name}} deleted",
  },
  oauth: {
    title: "OAuth connection ({{name}})",
    about: "This environment signs in with an OAuth 2 authorization code. Sagot keeps the tokens sealed and refreshes them.",
    status: { connected: "Connected", not_connected: "Not connected", expired: "Expired", revoked: "Revoked" },
    expires: "Access expires {{date}}",
    unknown: "We couldn't read the connection status.",
    connect: "Connect",
    reconnect: "Reconnect",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect this environment?",
    disconnectBody: "The tokens are revoked. Calls fail until someone connects again.",
    disconnected: "Disconnected",
  },
  auth: {
    title: "Authentication",
    type: "Authentication type",
    slotHint: "The NAME of the secret slot that holds the value.",
    listHint: "Separate scopes with spaces.",
    secretsNext: "Secret values (keys, tokens, passwords) are set in the Secrets card after you save. They are never part of this configuration.",
    rawLabel: "Configuration (JSON)",
    rawHint: "Names, URLs and secret slot placeholders only. Never paste a secret value here.",
    problems: { required: "This field is required.", rawInvalid: "This is not valid JSON." },
    types: {
      none: "None",
      api_key_header: "Key sent to your system (header)",
      api_key_query: "Key sent to your system (query string)",
      bearer_token: "Bearer token",
      basic: "Basic (user name and password)",
      hmac_signing: "HMAC request signing",
      oauth2_client_credentials: "OAuth 2 client credentials",
      oauth2_authorization_code: "OAuth 2 authorization code (with refresh)",
      oauth2_custom_token: "Custom token endpoint",
      integration: "Managed integration (Zoho, Google)",
      mtls: "Client certificate (mTLS)",
    },
    about: {
      none: "The system needs no credentials.",
      api_key_header: "Sends the key in a header you name, with an optional prefix like Token.",
      api_key_query: "Sends the key as a query parameter you name.",
      bearer_token: "Sends Authorization: Bearer <token>.",
      basic: "Sends the user name and password as HTTP Basic.",
      hmac_signing: "Signs each request with a shared secret and sends the signature in a header.",
      oauth2_client_credentials: "Fetches an access token from the token URL with a client id and secret, and refreshes it.",
      oauth2_authorization_code: "A person connects once through the provider's consent screen; Sagot refreshes the tokens.",
      oauth2_custom_token: "A token endpoint that is not standard OAuth 2: describe the request and where to read the token.",
      integration: "Reuses the tokens of an integration that is already connected.",
    },
    fields: {
      header_name: "Header name",
      param_name: "Query parameter name",
      secret: "Secret slot",
      prefix: "Value prefix",
      scheme: "Scheme",
      username_secret: "User name slot",
      password_secret: "Password slot",
      algorithm: "Algorithm",
      signature_header: "Signature header",
      string_to_sign: "String to sign",
      signature_encoding: "Signature encoding",
      timestamp_header: "Timestamp header",
      token_url: "Token URL",
      authorization_url: "Authorization URL",
      client_id_secret: "Client id slot",
      client_secret_secret: "Client secret slot",
      client_auth: "Send the client credentials",
      scopes: "Scopes",
      pkce: "Use PKCE",
      integration: "Integration",
    },
    options: {
      client_auth: { basic: "In a Basic header", body: "In the request body" },
      algorithm: { sha256: "SHA-256", sha512: "SHA-512" },
      signature_encoding: { hex: "Hex", base64: "Base64", base64url: "Base64 (URL-safe)" },
      integration: { zoho: "Zoho", google: "Google" },
    },
  },
};
